<template>
  <div>
    <v-alert
      color="var(--orange-100)"
      class="quote-expired-message mb-5"
      v-if="isExpired"
      dense
    >
      <div class="d-flex flex-no-wrap error-message">
        <div class="mr-3">
          <v-icon color="var(--orange-500)">mdi-alert</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> This quote has expired.
          Please check all the details below are accurate and submit a new
          quote.
        </div>
      </div>
    </v-alert>
    <v-alert
      color="var(--red-100)"
      class="quote-error-message"
      v-if="quoteFailed && !isDrawer"
      dense
    >
      <div class="d-flex flex-no-wrap error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> We couldn't get quotes.
          Please check your internet connection and try again in a few minutes.
        </div>
      </div>
    </v-alert>
    <v-alert
      color="var(--red-100)"
      class="quote-error-message"
      v-if="quoteDeclined && !isDrawer"
      dense
    >
      <div class="d-flex flex-no-wrap error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> Unfortunately no insurer
          was able to offer you a quote at this time based on the details you
          have entered.
        </div>
      </div>
    </v-alert>
    <v-alert
      color="var(--red-100)"
      class="quote-error-message"
      v-if="clientError && !isDrawer"
    >
      <div class="d-flex flex-no-wrap error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> There is a problem with
          the following questions:
          <ul>
            <li
              v-for="(message, index) in clientErrorMessagesArray"
              :key="index"
              class="font-weight-bold"
            >
              {{ message }}
            </li>
          </ul>
        </div>
      </div>
    </v-alert>
    <div
      class="elevation-3 customise-cover__container"
      style="background: white"
      :class="{ 'pa-4 px-xl-16 py-xl-8 rounded-lg': !isDrawer }"
      v-if="coverLocal"
    >
      <BaseButton
        v-if="!isDrawer"
        :text="`Back to '${section.PreviousStepTitle}'`"
        flat
        ripple
        class="font-weight-bold d-flex back-btn ml-n5 ml-xl-n16 mt-lg-n1 back-previous__btn-link"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToLastStep"
      />

      <v-row
        v-if="isDrawer"
        :class="{ 'fixed-section__wrapper header__wrapper': isDrawer }"
      >
        <v-col cols="12">
          <div
            class="drawer-header d-flex align-center justify-space-between pa-5"
          >
            <h3 class="font-28 font-weight-bold">Customise your cover</h3>
            <v-btn
              icon
              @click="
                () => {
                  closeDrawer();
                  setDataToStore();
                }
              "
              class="btn-close"
            >
              <v-icon size="32">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <div
        :class="{
          'customise-drawer-body': isDrawer,
          'mb-108px': isDrawer && isValuablesOverLimit,
          'mb-132px': isDrawer && isPPItemsWorthOverLimit,
          'mb-240px':
            isDrawer && isValuablesOverLimit && isPPItemsWorthOverLimit,
        }"
      >
        <div v-if="isRefreshingCover" class="overlay"></div>
        <div
          class="pt-3 pl-2 pr-2 pb-7 pl-lg-5 pr-lg-10 pb-lg-9"
          v-if="isDrawer"
        >
          <div class="d-flex bg-blue-50 pa-3 align-center rounded">
            <v-icon class="mr-2" color="var(--blue-500)"
              >mdi-information</v-icon
            >
            <span style="max-width: 287px">
              We’re showing you policies with
              <span class="font-weight-bold">{{ coverType }}</span> cover.
            </span>
            <div class="flex-grow-1"></div>
            <BaseButton
              text="Edit cover"
              flat
              link
              icon="mdi-pencil"
              depressed
              color="#4d7fb8"
              ripple
              small
              class="px-1 d-flex align-self-center font-weight-bold"
              @click="editCover"
            />
          </div>
        </div>

        <div :class="{ 'px-7 pl-lg-10 pr-lg-11': isDrawer }">
          <v-row v-if="!isDrawer && (isBuildingsCover || isContentsCover)">
            <v-col cols="12">
              <div class="d-flex justify-space-between flex-wrap">
                <h1 class="text-bold font-28 font-weight-bold mt-6">
                  Choose your cover levels
                </h1>
                <BaseButton
                  text="Save &amp; exit"
                  flat
                  depressed
                  ripple
                  class="font-weight-bold d2c-save-exit mt-8 d-flex"
                  icon="mdi-content-save"
                  color="#106175"
                  small
                  @click="loadSaveAndExitDialog()"
                />
              </div>
              <p class="pt-5 mb-0 mb-lg-4">
                You can choose to add optional extras to your policy to give you
                even more protection. Adding home emergency cover or bicycle
                cover to your Home Insurance can be cheaper than getting them
                separately.
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" v-if="isBuildingsCover">
              <h4 class="font-20" :class="isDrawer ? 'mb-4' : 'mb-2'">
                Buildings Insurance
                <span
                  v-if="isDrawer"
                  class="align-self-start accent--text font-weight-semi-bold text-cursor"
                  href="javascript:"
                  @click="showModal('DialogContentCustomiseBuildingInsurance')"
                >
                  <v-icon color="#106175" size="15"
                    >mdi-help-circle</v-icon
                  ></span
                >
              </h4>
              <div :class="{ 'border-r10 px-3 py-9 px-lg-10': !isDrawer }">
                <div
                  class="d-flex flex-column flex-lg-row align-xl-center mb-7"
                  :class="isDrawer ? ' mb-lg-8' : ' mb-lg-7'"
                >
                  <div
                    class="flex-wrap mr-lg-2 flex-basis-58 left-panel__money-spinner"
                  >
                    <p
                      class="mb-2"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      Buildings cover: £1 million
                    </p>
                    <v-alert
                      color="var(--blue-100)"
                      class="d-none d-lg-block"
                      v-if="!isDrawer"
                    >
                      <div class="d-flex flex-no-wrap">
                        <div class="mr-1">
                          <v-icon color="var(--blue-500)"
                            >mdi-information</v-icon
                          >
                        </div>
                        <div class="note__wrapper">
                          <strong>Note:</strong> The buildings cover amount must
                          cover the cost of rebuilding your home.
                        </div>
                      </div>
                    </v-alert>
                  </div>
                  <BaseMoneySpinner
                    :small="$vuetify.breakpoint.lgAndUp ? true : false"
                    :config="getBuildingConfigD2C"
                    v-model="coverLocal.buildingsCover.amount"
                    class="mt-3 mt-xl-0 pa-0 flex-grow-1"
                  />
                  <v-alert
                    color="var(--blue-100)"
                    class="d-block d-lg-none mt-2"
                    v-if="!isDrawer"
                  >
                    <div class="d-flex flex-no-wrap">
                      <div class="mr-1">
                        <v-icon color="var(--blue-500)">mdi-information</v-icon>
                      </div>
                      <div class="note__wrapper">
                        <strong>Note:</strong> The buildings cover amount must
                        cover the cost of rebuilding your home.
                      </div>
                    </div>
                  </v-alert>
                </div>
                <div
                  class="d-flex flex-column flex-lg-row mb-9 align-xl-center"
                >
                  <div
                    class="flex-wrap mr-lg-2 flex-basis-58 left-panel__money-spinner"
                  >
                    <p
                      class="ma-0 pr-lg-6 pr-0"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      Choose your buildings cover excess
                      <span
                        v-if="!isDrawer"
                        class="d-inline-block d-md-none align-self-start accent--text font-weight-semi-bold text-cursor"
                        href="javascript:"
                        @click="showModal('DialogContentBuildingCoverExcess')"
                      >
                        <v-icon color="#106175" size="15"
                          >mdi-help-circle</v-icon
                        >
                      </span>
                    </p>
                    <span
                      class="align-center d-md-inline-flex d-none"
                      v-if="!isDrawer"
                    >
                      <a
                        class="align-self-start accent--text font-weight-semi-bold"
                        href="javascript:"
                        @click="showModal('DialogContentBuildingCoverExcess')"
                      >
                        What amount is right for me?
                      </a>
                    </span>
                  </div>
                  <BaseMoneySpinner
                    :small="$vuetify.breakpoint.lgAndUp ? true : false"
                    :config="configExcess"
                    v-model="coverLocal.buildingsCover.excess"
                    class="mt-5 mt-xl-0 pa-0 flex-grow-1"
                  />
                </div>
                <div class="d-flex flex-column flex-lg-row">
                  <div class="d-flex flex-column left-panel__toggle-button">
                    <p
                      class="ma-0 pr-lg-6 pr-0"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      Would you like to add extra accidental damage cover for
                      buildings?
                      <span
                        class="d-inline-block d-md-none text-cursor"
                        v-if="!isDrawer"
                        @click="
                          showModal('DialogContentBuildingExtraAccidentalCover')
                        "
                      >
                        <v-icon color="#106175" size="15"
                          >mdi-help-circle</v-icon
                        ></span
                      >
                    </p>
                    <span
                      class="align-center d-none d-md-flex"
                      v-if="!isDrawer"
                    >
                      <a
                        class="align-self-start accent--text font-weight-semi-bold"
                        href="javascript:"
                        @click="
                          showModal('DialogContentBuildingExtraAccidentalCover')
                        "
                      >
                        What’s covered as standard?</a
                      >
                    </span>
                  </div>
                  <div
                    class="col-lg-4 pa-0 d-flex align-center mt-5 mt-lg-0 ml-lg-auto toggle-button__wrapper"
                  >
                    <BaseToggleButton
                      v-model="addBuildingAD"
                      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    />
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              v-if="isContentsCover"
              :class="isDrawer ? 'pt-10' : 'mt-3'"
            >
              <h4 class="font-20" :class="isDrawer ? 'mb-4' : 'mb-2'">
                Contents Insurance
                <span
                  v-if="isDrawer"
                  class="align-self-start accent--text font-weight-semi-bold text-cursor"
                  href="javascript:"
                  @click="showModal('DialogContentCustomiseContentsInsurance')"
                >
                  <v-icon color="#106175" size="15"
                    >mdi-help-circle</v-icon
                  ></span
                >
              </h4>
              <div
                class=""
                :class="{ 'border-r10 px-3 py-9 px-lg-10': !isDrawer }"
              >
                <div
                  class="d-flex flex-column flex-lg-row align-xl-center"
                  :class="isDrawer ? ' mb-lg-8' : ' mb-lg-11'"
                >
                  <div
                    class="d-flex flex-column flex-basis-58 mr-lg-2 left-panel__money-spinner"
                  >
                    <p
                      class="mb-2"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      What amount of Contents Insurance is required?
                    </p>
                    <v-alert
                      color="var(--blue-100)"
                      class="d-none d-lg-block mb-0"
                      v-if="!isDrawer"
                    >
                      <div class="d-flex flex-no-wrap">
                        <div class="mr-1">
                          <v-icon color="var(--blue-500)"
                            >mdi-information</v-icon
                          >
                        </div>
                        <div class="note__wrapper">
                          <strong>Note:</strong> The amount selected must cover
                          the cost of replacing all of your contents as new.
                        </div>
                      </div>
                    </v-alert>
                  </div>
                  <BaseMoneySpinner
                    :small="$vuetify.breakpoint.lgAndUp ? true : false"
                    v-model="coverLocal.contentsCover.amount"
                    :config="getContentConfigD2C"
                    class="mt-3 mt-xl-0 pa-0 flex-grow-1"
                  />
                </div>
                <v-alert
                  color="var(--blue-100)"
                  class="d-block d-lg-none mt-2"
                  v-if="!isDrawer"
                >
                  <div class="d-flex flex-no-wrap">
                    <div class="mr-1">
                      <v-icon color="var(--blue-500)">mdi-information</v-icon>
                    </div>
                    <div class="note__wrapper">
                      <strong>Note:</strong> The amount selected must cover the
                      cost of replacing all of your contents as new.
                    </div>
                  </div>
                </v-alert>
                <div
                  class="d-flex flex-column flex-lg-row align-xl-center mt-7"
                >
                  <div
                    class="d-flex flex-column flex-basis-58 mr-lg-2 left-panel__money-spinner"
                  >
                    <p
                      class="ma-0 pr-lg-6 pr-0"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      Choose your contents cover excess
                      <span
                        v-if="!isDrawer"
                        class="d-inline-block d-md-none align-self-start accent--text font-weight-semi-bold text-cursor"
                        href="javascript:"
                        @click="showModal('DialogContentCoverExcess')"
                      >
                        <v-icon color="#106175" size="15"
                          >mdi-help-circle</v-icon
                        ></span
                      >
                    </p>
                    <span
                      class="align-center d-md-inline-flex d-none"
                      v-if="!isDrawer"
                    >
                      <a
                        class="align-self-start accent--text font-weight-semi-bold"
                        href="javascript:"
                        @click="showModal('DialogContentCoverExcess')"
                      >
                        What amount is right for me?</a
                      >
                    </span>
                  </div>
                  <BaseMoneySpinner
                    :small="$vuetify.breakpoint.lgAndUp ? true : false"
                    :config="configExcess"
                    v-model="coverLocal.contentsCover.excess"
                    class="mt-5 mt-xl-0 pa-0 flex-grow-1"
                  />
                </div>
                <div class="d-flex flex-column flex-lg-row mt-9">
                  <div class="d-flex flex-column left-panel__toggle-button">
                    <p
                      class="ma-0 pr-lg-6 pr-0"
                      :class="
                        isDrawer ? 'font-16' : 'font-18 font-weight-medium'
                      "
                    >
                      Would you like to add extra accidental damage cover for
                      contents?
                      <span
                        class="d-inline-block d-md-none text-cursor"
                        v-if="!isDrawer"
                        @click="showModal('DialogContentExtraAccidentalCover')"
                      >
                        <v-icon color="#106175" size="15"
                          >mdi-help-circle</v-icon
                        ></span
                      >
                    </p>
                    <span
                      class="align-center d-none d-md-flex"
                      v-if="!isDrawer"
                    >
                      <a
                        class="align-self-start accent--text font-weight-semi-bold"
                        color="#106175"
                        href="javascript:"
                        @click="showModal('DialogContentExtraAccidentalCover')"
                      >
                        What’s covered as standard?</a
                      >
                    </span>
                  </div>
                  <div
                    class="col-lg-4 pa-0 d-flex align-center mt-5 mt-lg-0 ml-lg-auto toggle-button__wrapper"
                  >
                    <BaseToggleButton
                      v-model="addContentsAD"
                      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    />
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="isContentsCover">
            <v-col cols="12" class="pb-0">
              <h3
                class="font-weight-bold mb-5"
                :class="isDrawer ? 'font-20 mt-7' : 'font-28 mt-2'"
              >
                Would you like to specify items?
              </h3>
              <p class="mb-4">
                You can choose to specify valuable items to make sure they are
                fully insured. Any items which cost less than £2,500 are already
                covered as standard.
              </p>
            </v-col>
            <v-col cols="12">
              <div
                class="border-r10 px-3 py-4 px-lg-6 pb-0 cover-content-valuables__wrapper"
              >
                <D2CDrawerCustomiseCoverContentValuables
                  :cover="coverLocal"
                  :isSelfserve="true"
                  :isDrawer="isDrawer"
                  :isShowTitle="false"
                  :VALUABLES="VALUABLES_D2C"
                  @showWhatsIncluded="showWhatsIncluded"
                  @isValuablesOverLimitUpdate="
                    (UpdatedLimit) => (isValuablesOverLimit = UpdatedLimit)
                  "
                />
              </div>
            </v-col>
          </v-row>

          <v-row v-if="isContentsCover">
            <v-col cols="12" class="pb-0">
              <h3
                class="font-weight-bold mb-5"
                :class="isDrawer ? 'font-20 mt-6' : 'font-28 mt-2'"
              >
                Would you like to cover items away from the home?
              </h3>
              <p class="mb-4">
                You can choose to specify items that you would like to cover for
                loss or damage when taken away from the home.
              </p>
            </v-col>
            <v-col class="person-possessions__wrapper">
              <template>
                <div
                  class="border-r10 px-3 py-4 px-lg-6 mb-6"
                  v-for="(possession, index) in PERSONAL_POSSESSIONS_D2C"
                  :key="possession.name"
                >
                  <div
                    class="d-flex flex-column flex-lg-row align-xl-center item__wrapper"
                  >
                    <div class="col-lg-9 d-flex flex-row pa-0">
                      <v-col
                        v-if="possession?.imageName"
                        class="pl-0 pr-6 py-0"
                      >
                        <v-img
                          :src="
                            require(`@/assets/images/customise-cover/${possession?.imageName}`)
                          "
                          transition="scale-transition"
                          width="118"
                          contain
                          :alt="possession?.imageName"
                        />
                      </v-col>
                      <div class="d-flex flex-column">
                        <h4 class="font-18 mb-2">
                          {{ possession.sectionHeading }}
                          <span
                            v-if="
                              isDrawer ||
                              (!isDrawer && $vuetify.breakpoint.mobile)
                            "
                            class="align-self-start accent--text font-weight-semi-bold ml-2 link-popup text-cursor"
                            href="javascript:"
                            @click="
                              showModal(
                                possession?.modalData?.dialogComponentName
                              )
                            "
                          >
                            <v-icon color="#106175" size="15"
                              >mdi-help-circle</v-icon
                            ></span
                          >
                        </h4>
                        <p class="ma-0 pr-lg-6 pr-0">
                          {{ possession.description }}
                        </p>
                        <span
                          class="d-flex align-center mt-5"
                          v-if="!isDrawer && !$vuetify.breakpoint.mobile"
                        >
                          <a
                            class="align-self-start accent--text font-weight-semi-bold link-popup"
                            href="javascript:"
                            @click="
                              showModal(
                                possession?.modalData?.dialogComponentName
                              )
                            "
                          >
                            What's included in this?</a
                          >
                        </span>
                      </div>
                    </div>
                    <div
                      class="col-lg-3 pa-0 d-flex align-center mt-5 mt-lg-0 mb-4"
                    >
                      <BaseToggleButton
                        v-model="possession.form.isShow"
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                      />
                    </div>
                  </div>
                  <div v-if="possession.form.isShow" class="mb-4 form__wrapper">
                    <div
                      style="background-color: #354048"
                      class="d-flex font-16 font-weight-bold white--text"
                    >
                      <div
                        class="col-12"
                        :class="[
                          possession.form?.itemTypes?.length
                            ? 'col-lg-8'
                            : 'col-lg-7',
                        ]"
                      >
                        {{ possession.title }}
                      </div>
                      <div
                        class="d-none d-lg-flex"
                        :class="[
                          possession.form?.itemTypes?.length
                            ? 'col-lg-4'
                            : 'col-lg-5',
                        ]"
                      >
                        {{ possession.form.valueFieldLabel }}
                      </div>
                    </div>
                    <ValidationObserver
                      ref="form"
                      v-slot="{ passes, reset, invalid, errors }"
                    >
                      <form
                        @submit.prevent="
                          (e) =>
                            onSubmit(index, possession, passes, reset, e.target)
                        "
                      >
                        <div
                          class="d-flex flex-column"
                          style="background-color: #f3f3f3"
                        >
                          <LayoutDrawerCustomiseCoverFormBicycles
                            v-if="possession.name === 'bicycles'"
                            :possession="possession"
                            :warningText="'Electric & motorised bicycles are not covered.'"
                            :regexPattern="REGEXP_PP_ITEM_VALUE"
                          />

                          <D2C_LayoutDrawerCustomiseCoverFormSpecifiedItems
                            v-else-if="
                              possession.name === 'specifiedItemsAwayFromHome'
                            "
                            :possession="possession"
                            :regexPattern="REGEXP_PP_ITEM_VALUE"
                            @showWhatsIncluded="showWhatsIncluded"
                          />

                          <LayoutDrawerCustomiseCoverFormBase
                            v-else
                            :possession="possession"
                            :regexPattern="REGEXP_PP_ITEM_VALUE"
                          />
                          <template v-for="(error, index) in errors">
                            <v-alert
                              :key="index"
                              dense
                              text
                              type="error"
                              icon="mdi-alert-circle"
                              class="mr-3 mb-3 ml-3"
                              outlined
                              v-if="error.length"
                            >
                              <span class="black--text">
                                {{ error[0] }}
                              </span>
                            </v-alert>
                          </template>
                        </div>
                      </form>
                    </ValidationObserver>
                    <template v-if="possession.items.length">
                      <div
                        class="possession-container px-2 px-lg-0 mb-4"
                        v-for="(item, index) in possession.items"
                        :key="index"
                      >
                        <div class="border-r10 pa-3 mt-5">
                          <div class="d-flex justify-space-between">
                            <div class="flex-grow-1">
                              <div class="item-title font-12 pa-1 px-4">
                                {{
                                  possession.itemsTitle
                                    ? possession.itemsTitle
                                    : item.text
                                }}
                              </div>
                            </div>
                            <div class="d-none d-lg-flex">
                              <BaseButton
                                text="REMOVE"
                                flat
                                rounded
                                small
                                icon="mdi-minus-circle"
                                class="font-16"
                                @click="possession.items.splice(index, 1)"
                                :iconColor="'var(--red-500)'"
                                :link="true"
                              />
                            </div>
                          </div>
                          <div
                            class="d-flex py-4 justify-space-between font-weight-bold font-20 description__wrapper"
                          >
                            <h4 class="mr-6">{{ item.description }}</h4>
                            <h4>
                              {{
                                item.value
                                  | formatCurrency(AMOUNT_INSURED_FORMAT)
                              }}
                            </h4>
                          </div>
                        </div>
                        <div class="justify-center d-flex d-lg-none">
                          <BaseButton
                            text="REMOVE"
                            flat
                            rounded
                            small
                            icon="mdi-minus-circle"
                            class="font-16 mt-5"
                            @click="possession.items.splice(index, 1)"
                            :iconColor="'var(--red-500)'"
                            :link="true"
                          />
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
                <div class="border-r10 px-3 py-4 px-lg-6">
                  <div
                    class="d-flex flex-column flex-lg-row align-xl-center item__wrapper"
                  >
                    <div class="col-lg-9 d-flex flex-row pa-0">
                      <v-col class="pl-0 pr-6 py-0">
                        <v-img
                          :src="
                            require(`@/assets/images/customise-cover/${UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.imageName}`)
                          "
                          transition="scale-transition"
                          width="118"
                          contain
                          :alt="UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.imageName"
                        />
                      </v-col>
                      <div class="d-flex flex-column">
                        <h4 class="font-18 mb-2">
                          {{
                            UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.sectionHeading
                          }}
                          <span
                            v-if="
                              UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.modalData
                                ?.dialogComponentName &&
                              (isDrawer ||
                                ($vuetify.breakpoint.mobile && !isDrawer))
                            "
                            class="align-self-start accent--text font-weight-semi-bold ml-2 link-popup text-cursor"
                            href="javascript:"
                            @click="
                              showModal(
                                UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.modalData
                                  ?.dialogComponentName
                              )
                            "
                          >
                            <v-icon color="#106175" size="15"
                              >mdi-help-circle</v-icon
                            ></span
                          >
                        </h4>
                        <p class="mb-0">
                          {{ UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.description }}
                        </p>
                        <span
                          class="d-flex align-center mt-5"
                          v-if="
                            UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.modalData
                              ?.dialogComponentName &&
                            !isDrawer &&
                            !$vuetify.breakpoint.mobile
                          "
                        >
                          <a
                            class="align-self-start accent--text font-weight-semi-bold link-popup"
                            href="javascript:"
                            @click="
                              showModal(
                                UNSPECIFIED_PERSONAL_POSSESSIONS_D2C?.modalData
                                  ?.dialogComponentName
                              )
                            "
                          >
                            What's included in this?</a
                          >
                        </span>
                      </div>
                    </div>
                    <div
                      class="col-lg-3 pa-0 d-flex align-center mt-5 mt-lg-0 mb-4"
                    >
                      <BaseToggleButton
                        v-model="UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow"
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                      />
                    </div>
                  </div>
                  <div
                    v-if="UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow"
                    class="mb-4 form__wrapper"
                  >
                    <div
                      style="background-color: #354048"
                      class="d-flex justify-space-between font-16 font-weight-bold white--text"
                    >
                      <div class="col-12">Amount</div>
                    </div>
                    <div
                      class="d-flex flex-column pa-3"
                      style="background-color: #f3f3f3"
                    >
                      <BaseSelect
                        name="itemType"
                        hideDetails
                        :rules="{ required: true }"
                        :items="
                          isHigherLevelContentsCover
                            ? UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.higherUnspecifiedAmounts
                            : UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.standardUnspecifiedAmounts
                        "
                        :isEnableSortOrderListItems="false"
                        :label="''"
                        v-model="unspecifiedPpAmount"
                        :dense="$vuetify.breakpoint.lgAndUp"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <h3
                class="font-weight-bold mb-5"
                :class="isDrawer ? 'font-20 mt-6' : 'font-28 mt-2'"
              >
                Would you like to boost your cover?
                <v-icon
                  v-if="$vuetify.breakpoint.mobile"
                  color="#106175"
                  size="15"
                  class="text-cursor"
                  @click="
                    showModal('DialogContentHomeEmergencyAndLegalExpenses')
                  "
                  >mdi-help-circle</v-icon
                >
              </h3>
              <p v-if="isDrawer" :class="isDrawer ? 'mb-0' : 'mb-2'">
                You can choose to add optional extras to your policy to give you
                even more protection. Adding home emergency cover or legal
                expenses cover to your Home Insurance can be cheaper than
                getting them separately.
              </p>
            </v-col>
            <v-col class="addOn__wrapper" :class="isDrawer && 'mb-4'">
              <h4 class="font-20 mb-5" :class="isDrawer ? 'd-none' : ''">
                Additional cover options
              </h4>
              <div
                class="border-r10 d-xl-flex align-xl-center mb-5 px-3 pt-9 pb-7"
                :class="isDrawer ? ' px-lg-3' : ' px-lg-7 pt-lg-8'"
                v-for="option in ADD_ONS"
                :key="option.id"
              >
                <div class="d-flex left-panel__wrapper">
                  <v-img
                    :src="require('@/assets/images/' + option?.image)"
                    transition="scale-transition"
                    height="104"
                    width="72"
                    class="ml-1"
                    contain
                    :alt="option?.name"
                  />
                  <div :class="isDrawer ? ' pl-7' : 'pl-8'">
                    <h4 class="font-18">
                      {{ option.type }}
                    </h4>
                    <p class="mt-3">
                      {{ option.description }}
                    </p>
                    <h4 class="font-16 mb-4" v-if="!option.isAdded">
                      +{{ option.annualCost() | formatCurrency }} per year (ex
                      IPT)
                    </h4>
                    <div class="d-flex text--success mb-4" v-else>
                      <v-icon size="18" class="mr-1" color="#1d8649"
                        >mdi-check-circle</v-icon
                      >
                      <h4 class="text-6">Added to the policy</h4>
                    </div>
                    <p class="mb-0" v-if="!$vuetify.breakpoint.mobile">
                      <span class="d-flex align-center">
                        <a
                          class="align-self-start accent--text font-weight-semi-bold"
                          href="javascript:"
                          @click="showModal(option?.dialogComponentName)"
                        >
                          What's included in this?</a
                        >
                      </span>
                    </p>
                  </div>
                </div>
                <BaseButton
                  :text="option.isAdded ? 'REMOVE' : 'ADD'"
                  :min-width="isDrawer ? '120' : '100'"
                  rounded
                  depressed
                  :height="$vuetify.breakpoint.lgAndUp ? '52' : '60'"
                  :type="option.isAdded ? 'text' : ''"
                  :block="$vuetify.breakpoint.xl ? false : true"
                  :color="
                    option.isAdded ? '' : isDrawer ? 'psorange' : 'psdarkgreen'
                  "
                  :icon="option.isAdded ? 'mdi-minus-circle' : ''"
                  :class="
                    !option.isAdded
                      ? isDrawer
                        ? 'font-weight-bold black--text'
                        : 'font-weight-bold white--text'
                      : 'font-weight-regular border-1 background-white'
                  "
                  class="ml-auto py-6 mt-4 mt-xl-0 font-20"
                  iconColor="var(--red-500)"
                  @click="option.isAdded = !option.isAdded"
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
      <v-row
        :class="
          isDrawer ? 'fixed-section__wrapper footer__wrapper pb-4' : 'mt-1'
        "
      >
        <v-col>
          <v-divider
            v-if="isDrawer"
            class="mr-n7 ml-lg-n10 mr-lg-n11 pb-6 mt-n1 drawer__bottom--border"
          ></v-divider>
          <div v-if="isValuablesOverLimit" class="mb-5">
            <v-alert
              dense
              text
              type="error"
              icon="mdi-alert-circle"
              class="ma-0 flex-grow-1 rounded-lg"
            >
              <span class="black--text">
                <strong>Sorry! </strong>The total value of valuables must not
                exceed 30% of Contents cover. Please increase your Contents
                cover amount or reduce your valuables amount. Alternatively, if
                you still wish to cover these items, please contact us on
                <strong>{{
                  contactInfo?.PhoneNumber | VMask(PHONE_NUMBER_VMASK)
                }}</strong>
                to speak to a member of our team who may be able to assist you.
              </span>
            </v-alert>
          </div>
          <div v-if="isPPItemsWorthOverLimit" class="mb-5">
            <v-alert
              dense
              text
              type="error"
              icon="mdi-alert-circle"
              class="ma-0 flex-grow-1 rounded-lg"
            >
              <span class="black--text">
                <strong>Sorry! </strong>The total value of personal possessions
                and additional loss or damage must not exceed 30% of Contents
                cover. Please increase your Contents cover amount or reduce your
                personal possessions amount. Alternatively, if you still wish to
                cover these items, please contact us on
                <strong>{{
                  contactInfo?.PhoneNumber | VMask(PHONE_NUMBER_VMASK)
                }}</strong>
                to speak to a member of our team who may be able to assist you.
              </span>
            </v-alert>
          </div>

          <div :class="{ 'mx-11': isDrawer }">
            <BaseButton
              :text="isDrawer ? 'REFRESH QUOTES' : 'GET QUOTES'"
              :height="isDrawer ? '68' : '60'"
              :minWidth="$vuetify.breakpoint.lgAndUp ? '265' : '100%'"
              :block="isDrawer ? true : false"
              :color="isDrawer ? '#106175' : '#fab519'"
              class="font-weight-bold font-20 rounded-pill px-10 white--text btn-quote"
              :class="isDrawer ? 'white--text' : 'black--text'"
              type="submit"
              iconSize="30"
              :icon="isDrawer ? 'mdi-refresh' : ''"
              :loading="isRefreshingCover"
              loadingIconColor="white"
              @click="onConfirmEligibility"
              :disabled="
                isPPItemsWorthOverLimit ||
                isValuablesOverLimit ||
                isRefreshingCover
              "
            />
          </div>
        </v-col>
      </v-row>

      <BaseDialog
        ref="showWhatsIncludedPersonalPossessions"
        windowClass="pa-0 pb-xl-0"
        headerClass="pa-4 pb-5 mb-n1"
        :headerDivider="true"
        :persistent="true"
        @dialogCloseButton="dialogCloseButton"
      >
        <template v-slot:content>
          <DialogContentWhatsIncludedPersonalPossessions
            @cancelWhatsIncluded="cancelWhatsIncluded"
          />
        </template>
      </BaseDialog>
      <BaseDialog
        ref="showWhatsIncludedValuablesInHome"
        windowClass="pa-0 pb-xl-0"
        headerClass="pa-4 pb-5 mb-n1"
        headerDivider
        persistent
        @dialogCloseButton="dialogCloseButton"
      >
        <template v-slot:content>
          <DialogContentWhatsIncludedValuablesInHome
            @cancelWhatsIncluded="cancelWhatsIncluded"
          />
        </template>
      </BaseDialog>

      <EligibilityBaseDialog
        ref="dialogEligibility"
        :windowClass="'confirm-dialog'"
      >
        <template v-slot:content="{ cancel, isShow, title }">
          <Eligibility
            v-if="isShow"
            :handleNextData="{}"
            :cancel="cancel"
            :dialogRef="$refs.dialogEligibility"
            :dialogLoading="$refs.dialogLoading"
            :dialogNotMet="$refs.dialogNotMet"
            :isShow="isShow"
            :title="title"
            :listEligibility="HOME_ELIGIBILITY"
            :inforMsg="HOME_ELIGIBILITY_INFO_MSG"
            :customHandleConfirmEligibility="true"
            @confirmEligibility="getQuoteAfterConfirmEligibility"
          />
        </template>
      </EligibilityBaseDialog>
      <EligibilityBaseDialog
        ref="dialogLoading"
        :windowClass="'confirm-dialog'"
      >
        <template v-slot:content="{ title }">
          <EligibilityLoading :title="title" />
        </template>
      </EligibilityBaseDialog>

      <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
        <template v-slot:content="{ cancel, isShow, title }">
          <EligibilityNotMet
            :cancel="cancel"
            :dialogRef="$refs.dialogEligibility"
            :dialogNotMet="$refs.dialogNotMet"
            :isShow="isShow"
            :title="title"
            :isSelfserve="true"
            :emitEventToParent="true"
            @confirmEligibilityNotMet="confirmEligibilityNotMet"
          />
        </template>
      </EligibilityBaseDialog>

      <BaseDialog
        ref="saveAndExitDialog"
        headerIcon="mdi-content-save"
        headerIconColor="#106175"
        windowClass="pa-0 pb-xl-0"
        headerClass="d2c-dialog-header d2c-save-exit-header pa-4 mb-n1"
        headerDivider
        :max-width="800"
        :isSelfServe="isSelfServe"
        dark2Icon
      >
        <template v-slot:content="{}">
          <SaveAndExit
            :sectionReminder="section.SectionReminder"
            :showUserRef="!section.SectionReminder"
            @saveAndExit="saveAndExit"
            @close="closeSaveAndExitDialog"
          />
        </template>
      </BaseDialog>
    </div>
  </div>
</template>

<script>
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import { formAnswersToApiAnswers, includesContents } from "@/common/answers";
import { dereference } from "@/common/transforms";
import { hasConfirmedEligibility } from "@/common/quotes";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseDialog from "@/components/BaseComponents/BaseDialog.vue";
import BaseMoneySpinner from "@/components/BaseComponents/BaseMoneySpinner.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import D2C_LayoutDrawerCustomiseCoverFormSpecifiedItems from "@/components/LayoutComponents/D2C_LayoutDrawerCustomiseCoverFormSpecifiedItems.vue";
import LayoutDrawerCustomiseCoverFormBase from "@/components/LayoutComponents/LayoutDrawerCustomiseCoverFormBase.vue";
import LayoutDrawerCustomiseCoverFormBicycles from "@/components/LayoutComponents/LayoutDrawerCustomiseCoverFormBicycles.vue";
import D2CDrawerCustomiseCoverContentValuables from "@/components/QuoteResult/D2CDrawerCustomiseCoverContentValuables.vue";
import DialogContentWhatsIncludedPersonalPossessions from "@/components/QuoteResult/DialogContentWhatsIncludedPersonalPossessions";
import DialogContentWhatsIncludedValuablesInHome from "@/components/QuoteResult/DialogContentWhatsIncludedValuablesInHome";
import {
  CLIENTERROR,
  FAILED,
  PHONE_NUMBER_VMASK,
  TOOL_TIP_TEXT,
  UNABLE,
} from "@/constants/common.constants";
import { CONFIG_CONTENTS_D2C } from "@/constants/contents-config.constants";
import { CONFIG_EXCESS_D2C } from "@/constants/excess-config.constants";
import { ADD_ONS } from "@/constants/additional-cover-options";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
import {
  HOME_ELIGIBILITY,
  HOME_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import { REGEXP_PP_ITEM_VALUE } from "@/constants/regex-patterns.constants";
import quoteService from "@/services/quote.service.js";
import { QuestionStore } from "@/store/store";
import {
  BELONGINGS_MODULE,
  DIALOG_MODULE,
  QUESTIONS_MODULE,
  STEPS_MODULE,
} from "@/store/types/module-types";
import Eligibility from "@/views/Dialog/Eligibility";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet";
import numeral from "numeral";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "CustomiseCover",
  components: {
    EligibilityBaseDialog,
    EligibilityLoading,
    EligibilityNotMet,
    Eligibility,
    BaseButton,
    BaseMoneySpinner,
    BaseToggleButton,
    ValidationObserver,
    BaseSelect,
    BaseDialog,
    DialogContentWhatsIncludedPersonalPossessions,
    DialogContentWhatsIncludedValuablesInHome,
    D2CDrawerCustomiseCoverContentValuables,
    LayoutDrawerCustomiseCoverFormBase,
    LayoutDrawerCustomiseCoverFormBicycles,
    D2C_LayoutDrawerCustomiseCoverFormSpecifiedItems,
    SaveAndExit: () => import("@/views/Dialog/SaveAndExit"),
  },
  mixins: [BuildingsContentsVisibility],
  props: {
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
    refreshQuote: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDrawer: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    const getValuables = this.$store.getters["belongings/getValuables"];
    const getPersonalPossessions =
      this.$store.getters["belongings/getPersonalPossessions"];
    const getUnspecifiedPersonalPossessions =
      this.$store.getters["belongings/getUnspecifiedPersonalPossessions"];
    return {
      AMOUNT_INSURED_FORMAT,
      ADD_ONS,
      PHONE_NUMBER_VMASK,
      VALUABLES_D2C: dereference(getValuables),
      PERSONAL_POSSESSIONS_D2C: dereference(getPersonalPossessions),
      UNSPECIFIED_PERSONAL_POSSESSIONS_D2C: dereference(
        getUnspecifiedPersonalPossessions
      ),
      CONFIG_CONTENTS_D2C,
      TOOL_TIP_TEXT,
      REGEXP_PP_ITEM_VALUE,
      HOME_ELIGIBILITY,
      HOME_ELIGIBILITY_INFO_MSG,
      unspecifiedPpAmount: null,
      coverLocal: null,
      isRefreshingCover: false,
      addBuildingAD: false,
      addContentsAD: false,
      valueLocal: this.value,
      configExcess: CONFIG_EXCESS_D2C,
      navDrawerContent: null,
      isStateless: false,
      isValuablesOverLimit: false,
      cover: null,
      isAnnualRequote: QuestionStore.state("isAnnualRequote"),
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getAnswer",
      "getCover",
      "coverSelection",
      "isDrawerCustomiseCoverPopulatedFromApi",
      "isSelfServe",
      "getContentConfigD2C",
      "getBuildingConfigD2C",
      "getQuestionCoverType",
      "isNumberOfBedroomsOrBathRoomGreaterThan6",
      "isOwnershipPropertyRented",
      "contactInfo",
      "getAnswerObject",
      "isExpired",
      "httpConfig",
      "clientErrorMessagesArray",
    ]),
    ...mapState(QUESTIONS_MODULE, [
      "lastQuoteErrorType",
      "quoteAdditionalData",
      "questionMap",
      "isOriginalQuoteExpired",
    ]),
    ...mapGetters(BELONGINGS_MODULE, [
      "getValuables",
      "getPersonalPossessions",
      "getUnspecifiedPersonalPossessions",
    ]),
    quoteFailed() {
      return this.lastQuoteErrorType === FAILED;
    },
    quoteDeclined() {
      return this.lastQuoteErrorType === UNABLE;
    },
    clientError() {
      return this.lastQuoteErrorType === CLIENTERROR;
    },
    isHigherLevelContentsCover() {
      return (
        this.coverLocal.contentsCover.amount >= this.CONFIG_CONTENTS_D2C.default
      );
    },
    isPPItemsWorthOverLimit() {
      let ppAddedItemsTotalWorth = 0;
      let isPPItemsOverLimit;

      this.PERSONAL_POSSESSIONS_D2C.forEach((pp) => {
        if (!pp.form.isShow) return;
        pp.items.forEach((item) => {
          ppAddedItemsTotalWorth += parseFloat(item.value);
        });
      });

      if (this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow) {
        if (this.isHigherLevelContentsCover) {
          ppAddedItemsTotalWorth +=
            this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.higherUnspecifiedAmounts.find(
              (itemType) => itemType.value === this.unspecifiedPpAmount
            )?.numberValue;
        } else {
          ppAddedItemsTotalWorth +=
            this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.standardUnspecifiedAmounts.find(
              (itemType) => itemType.value === this.unspecifiedPpAmount
            )?.numberValue;
        }
      }

      isPPItemsOverLimit =
        ppAddedItemsTotalWorth >
        (this.coverLocal.contentsCover.amount / 100) * 30; // 30 percent of whatever contents cover is selected

      return isPPItemsOverLimit;
    },
    showLink() {
      return (data) => data && data.showLink;
    },
  },
  watch: {
    coverSelection(val) {
      this.setAccidentalDamageState(val);
    },
    addBuildingAD(val) {
      this.setCoverSelection({
        buildingsAd: val,
        contentsAd: this.addContentsAD,
      });
    },
    addContentsAD(val) {
      this.setCoverSelection({
        buildingsAd: this.addBuildingAD,
        contentsAd: val,
      });
    },
    value(val) {
      this.valueLocal = val;
    },
    lastQuoteErrorType(newValue) {
      if (newValue !== null) {
        this.scrollToErrors();
      }
    },
    getCover(val) {
      this.cover = val;
      this.coverLocal = dereference(this.getCover);
      this.setUnspecifiedPersonalPossVisibility();
    },
    getQuestionCoverType: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && !includesContents(val)) {
          this.PERSONAL_POSSESSIONS_D2C.forEach((possession) => {
            possession.form.isShow = false;
            possession.items = [];
          });
          this.VALUABLES_D2C.forEach((valuable) => {
            valuable.form.isShow = false;
            valuable.items = [];
          });
        }
      },
    },
    getContentConfigD2C() {
      this.initDefaultValue();
    },
    getValuables(value) {
      this.VALUABLES_D2C = dereference(value);
    },
    getPersonalPossessions(value) {
      this.PERSONAL_POSSESSIONS_D2C = dereference(value);
    },
    getUnspecifiedPersonalPossessions(value) {
      this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C = dereference(value);
    },
  },
  mounted() {
    this.cover = this.getCover;
    this.coverLocal = dereference(this.getCover);
    this.setAccidentalDamageState(this.coverSelection);
    if (!this.isDrawerCustomiseCoverPopulatedFromApi) {
      this.loadPersonalPossessions();
      this.loadValuables();
      this.loadAddOns();
      this.setIsDrawerCustomiseCoverPopulatedFromApi(true);
    }
    this.setUnspecifiedPersonalPossVisibility();
    this.initDefaultValue();
    this.getAppConfigurationContactInfo();
  },
  methods: {
    ...mapActions(STEPS_MODULE, ["handleNextWithoutRef", "handleBack"]),
    ...mapActions(QUESTIONS_MODULE, [
      "setCoverAnswers",
      "getQuotes",
      "setCoverSelection",
      "setIsDrawerCustomiseCoverPopulatedFromApi",
      "handleSaveAndExit",
      "handleD2CSaveAndExit",
      "getAppConfigurationContactInfo",
      "setIsExpiredFlag",
      "setHomeEligibility",
    ]),
    ...mapActions(BELONGINGS_MODULE, [
      "setValuables",
      "setPersonalPossessions",
      "setUnspecifiedPersonalPossessions",
    ]),
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    scrollToErrors() {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 300);
    },
    setAccidentalDamageState(coverSelection) {
      this.addBuildingAD = coverSelection.buildingsAd;
      this.addContentsAD = coverSelection.contentsAd;
    },
    loadSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.open("Save & exit");
    },
    saveAndExit(reference) {
      this.formDataToAnswer();
      this.$refs.saveAndExitDialog.cancel();
      if (this.isSelfServe) {
        this.handleD2CSaveAndExit();
      } else {
        this.handleSaveAndExit(reference);
      }
    },
    confirmEligibilityNotMet() {
      this.formDataToAnswer();
      this.handleD2CSaveAndExit();
      this.$refs.dialogNotMet.cancel();
    },
    closeSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.cancel();
    },
    onSubmit(index, possession, passes, resetForm, nativeForm) {
      passes(() => {
        this.PERSONAL_POSSESSIONS_D2C[index].items.push({
          description: possession.form.descriptionFieldValue,
          value: this.removeComma(possession.form.valueFieldValue),
          itemType: possession.form.selectedItemType,
          text: possession.form.itemTypes.find(
            (itemType) => itemType.value === possession.form.selectedItemType
          )?.text,
        });

        this.PERSONAL_POSSESSIONS_D2C[index].form.descriptionFieldValue = null;
        this.PERSONAL_POSSESSIONS_D2C[index].form.valueFieldValue = null;
        this.PERSONAL_POSSESSIONS_D2C[index].form.selectedItemType = null;
        this.$nextTick(() => {
          resetForm();
        });

        nativeForm
          ?.querySelectorAll(".money-format input")
          .forEach((input) => (input.value = ""));
      });
    },
    async getQuoteAfterConfirmEligibility() {
      const request = {
        QuoteAccessId: this.quoteAdditionalData.QuoteAccessId,
        IsExpiredQuoteConfirmed: this.isOriginalQuoteExpired,
      };

      await quoteService.confirmEligibility(request, this.httpConfig);
      this.quoteAdditionalData.IsEligibilityConfirmed = true;
      this.quoteAdditionalData.IsExpiredQuoteConfirmed =
        this.isOriginalQuoteExpired;
      this.isAnnualRequote = false;

      this.getQuote();
    },
    getQuote() {
      if (this.isRefreshingCover) return;
      this.setHomeEligibility("True");
      this.isRefreshingCover = true;
      if (!this.isDrawer) {
        this.$refs.dialogLoading.open("Hang tight, getting quotes...");
      }

      this.formDataToAnswer();

      const quoteRequestFields = {
        IsPartialQuote: false,
        SetDefaultUnapprovedMortgageApplication:
          this.section.SetDefaultUnapprovedMortgageApplication,
      };
      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.isRefreshingCover = false;
          this.valueLocal = false;
          this.$emit("refreshCover");
          this.setIsExpiredFlag(false);
          this.closeDrawer();
          if (!this.isDrawer) {
            this.$refs.dialogLoading.cancel();
          }
          if (!this.refreshQuote) {
            this.handleNextWithoutRef({
              section: this.section,
            });
          }
          this.setValuables(this.VALUABLES_D2C);
          this.setPersonalPossessions(this.PERSONAL_POSSESSIONS_D2C);
          this.setUnspecifiedPersonalPossessions(
            this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C
          );
        })
        .catch((error) => {
          console.log("refreshCover error", error);
          this.isRefreshingCover = false;
          this.valueLocal = false;
          if (!this.isDrawer) {
            this.$refs.dialogLoading.cancel();
          }
        });
    },
    formDataToAnswer() {
      const form = {
        buildingsCover: {
          ...this.coverLocal.buildingsCover,
        },
        contentsCover: {
          ...this.coverLocal.contentsCover,
        },
        additionalCoverOptions: [],
        personalPossessions: [],
        unspecifiedPpAmount: null,
      };

      this.ADD_ONS.forEach((option) => {
        form.additionalCoverOptions.push({
          isAdded: option.isAdded,
          code: option.code,
        });
      });

      for (const key in this.VALUABLES_D2C) {
        if (
          this.VALUABLES_D2C[key].form.isShow &&
          this.VALUABLES_D2C[key].items.length
        ) {
          form.personalPossessions.push({
            interfaceKeyPrefix: this.VALUABLES_D2C[key].interfaceKeyPrefix,
            items: this.VALUABLES_D2C[key].items,
          });
        }
      }

      for (const key in this.PERSONAL_POSSESSIONS_D2C) {
        if (
          this.PERSONAL_POSSESSIONS_D2C[key].form.isShow &&
          this.PERSONAL_POSSESSIONS_D2C[key].items.length
        ) {
          form.personalPossessions.push({
            interfaceKeyPrefix:
              this.PERSONAL_POSSESSIONS_D2C[key].interfaceKeyPrefix,
            items: this.PERSONAL_POSSESSIONS_D2C[key].items,
          });
        }
      }

      if (this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow) {
        form.unspecifiedPpAmount = this.unspecifiedPpAmount;
      }

      const answers = formAnswersToApiAnswers(form);
      this.setCoverAnswers(answers);
    },
    removeComma(value) {
      return value != null ? numeral(value).value() : null;
    },
    loadAddOns() {
      ADD_ONS.forEach((ao) => {
        if (ao.code === "he") ao.isAdded = this.cover.homeEmergency;
        if (ao.code === "le") ao.isAdded = this.cover.legalExpenses;
      });
    },
    loadPersonalPossessions() {
      this.PERSONAL_POSSESSIONS_D2C.forEach((pp) => {
        this.loadPpItems(pp);
      });
      this.setPersonalPossessions(this.PERSONAL_POSSESSIONS_D2C);
    },
    loadValuables() {
      this.VALUABLES_D2C.forEach((valuable) => {
        this.loadValuableItems(valuable);
      });
      this.setValuables(this.VALUABLES_D2C);
    },
    loadPpItems(personalPossessions) {
      this.cover.ppItems.forEach((ppItemGroup) => {
        const apiPPItem = ppItemGroup.find(
          (item) => item.InterfaceKey === "PPItemType"
        );
        if (!apiPPItem) return;

        let itemText = "";
        const apiPPType = ppItemGroup.find((item) => {
          if (item.Value === personalPossessions.form.selectedItemType) {
            return item;
          }

          const localPPItemType = personalPossessions.form.itemTypes.find(
            (ppType) => ppType.value === item.Value
          );
          if (localPPItemType && item.Value === localPPItemType.value) {
            itemText = localPPItemType.text;
            return item;
          }
        });

        if (!apiPPType) return;
        personalPossessions.form.isShow = true;

        let item = {
          text: itemText,
          description: ppItemGroup.find(
            (item) => item.InterfaceKey === "PPItemDescription"
          )?.Value,
          value: ppItemGroup.find((item) => item.InterfaceKey === "PPItemValue")
            ?.Value,
          itemType: apiPPType?.Value,
        };
        personalPossessions.items.push(item);
      });
    },
    loadValuableItems(valuable) {
      this.cover.ppItems.forEach((valuableItemGroup) => {
        const apiValuableItemType = valuableItemGroup.find(
          (item) => item.InterfaceKey === "SpecifiedItemType"
        );
        if (!apiValuableItemType) return;

        const localValuableItemType = valuable.form.itemTypes.find(
          (valuableType) => valuableType.value === apiValuableItemType.Value
        );

        if (!localValuableItemType) return;
        valuable.form.isShow = true;

        let item = {
          text: localValuableItemType.text,
          description: valuableItemGroup.find(
            (item) => item.InterfaceKey === "SpecifiedItemDescription"
          )?.Value,
          value: valuableItemGroup.find(
            (item) => item.InterfaceKey === "SpecifiedItemValue"
          )?.Value,
          itemType: apiValuableItemType?.Value,
        };
        valuable.items.push(item);
      });
    },
    cancelWhatsIncluded(ref) {
      this.$refs[ref].cancel();
    },
    showWhatsIncluded(data) {
      if (data.ref) {
        this.$refs[data.ref].open(`${data.title}`);
      } else {
        this.showModal(data.dialogComponentName);
      }
    },
    dialogCloseButton(value) {
      this.isStateless = value;
    },
    setUnspecifiedPersonalPossVisibility() {
      if (
        !!this.cover.unspecifiedPpAmount &&
        this.cover.unspecifiedPpAmount !== "Zero"
      ) {
        this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow = true;
        this.unspecifiedPpAmount = this.cover.unspecifiedPpAmount ?? null;
      } else {
        this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C.isShow = false;
      }
      this.setUnspecifiedPersonalPossessions(
        this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C
      );
    },
    initDefaultValue() {
      if (
        this.coverLocal.buildingsCover.amount === 0 ||
        (this.coverLocal.buildingsCover.amount <
          this.getBuildingConfigD2C.default &&
          this.isNumberOfBedroomsOrBathRoomGreaterThan6)
      ) {
        this.coverLocal.buildingsCover.amount =
          this.getBuildingConfigD2C.default;
      }
      if (this.coverLocal.buildingsCover.excess === 0) {
        this.coverLocal.buildingsCover.excess = this.configExcess.default;
      }
      if (
        this.coverLocal.contentsCover.amount === 0 ||
        (this.coverLocal.contentsCover.amount <
          this.getContentConfigD2C.default &&
          this.isNumberOfBedroomsOrBathRoomGreaterThan6) ||
        (this.coverLocal.contentsCover.amount > this.getContentConfigD2C.max &&
          this.isOwnershipPropertyRented)
      ) {
        this.coverLocal.contentsCover.amount = this.getContentConfigD2C.default;
      }
      if (this.coverLocal.contentsCover.excess === 0) {
        this.coverLocal.contentsCover.excess = this.configExcess.default;
      }
    },
    closeDrawer() {
      this.$emit("closeDrawer");
    },
    onConfirmEligibility() {
      if (
        (!this.quoteAdditionalData.IsEligibilityConfirmed &&
          (this.isAnnualRequote || !this.hasConfirmedEligibility())) ||
        (!this.quoteAdditionalData.IsExpiredQuoteConfirmed &&
          this.isOriginalQuoteExpired)
      ) {
        this.$refs.dialogEligibility.open("Eligibility check");
        return;
      }
      this.getQuote();
    },
    hasConfirmedEligibility() {
      const homeEligibility = this.getAnswerObject("HomeEligibility");
      return hasConfirmedEligibility(homeEligibility);
    },
    backToLastStep() {
      this.setDataToStore();
      this.handleBack({ section: this.section });
    },
    editCover() {
      this.setDataToStore();
      this.$router.push("/");
    },
    setDataToStore() {
      this.formDataToAnswer();
      this.setValuables(this.VALUABLES_D2C);
      this.setPersonalPossessions(this.PERSONAL_POSSESSIONS_D2C);
      this.setUnspecifiedPersonalPossessions(
        this.UNSPECIFIED_PERSONAL_POSSESSIONS_D2C
      );
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";
</style>
